import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';

import {NavigationEnd, Router} from '@angular/router';
import {filter} from 'rxjs/operators';
import {ClientConfiguration} from '../utils';
import {NGXLogger} from 'ngx-logger';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject} from 'rxjs';
import {FontFileService} from '@twpub/core/services/font-file.service';
import {FontFile} from '@twpub/core/models/font-file';
import {CssVariableDefinition} from '@twpub/core/models';
import {url as httpUrl} from '@twpub/core/utils/http-utils';

@Injectable({
  providedIn: 'root'
})
export class CssService {
  private loadedFonts: Set<string> = new Set();
  private loadedFontIds: Set<number> = new Set();
  private currentConfig: ClientConfiguration | null = null;
  private fontLoadPromises: Map<string, Promise<any>> = new Map();
  private cssVariablesDefaults: Map<string, string> = new Map();
  private configChangeSubject = new BehaviorSubject<number>(0);

  private static defaultFonts = [
    'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500&display=swap',
    'https://fonts.googleapis.com/icon?family=Material+Icons|Material+Icons+Outlined',
    'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200'
  ];

  // Web safe fonts that are available on most systems
  private static webSafeFonts = [
    'Arial', 'Helvetica', 'Times New Roman', 'Times', 'Courier New',
    'Courier', 'Verdana', 'Georgia', 'Palatino', 'Garamond', 'Bookman',
    'Comic Sans MS', 'Trebuchet MS', 'Arial Black', 'Impact', 'sans-serif', 'serif'
  ];

  private availableCssVariables: CssVariableDefinition[] = [];

  constructor(private logger: NGXLogger, @Inject(DOCUMENT) private document: Document, private router: Router,
              private http: HttpClient, private fontService: FontFileService
  ) {
    this.loadCssVariablesDefaults();
    this.listenForRouteChanges();
  }

  /**
   * Load CSS variables defaults from JSON file
   */
  private loadCssVariablesDefaults(): void {
    const url = 'assets/css-variables.json';
    this.http.get<CssVariableDefinition[]>(url)
      .subscribe({
        next: (variables) => {
          this.availableCssVariables = variables;
          variables.forEach(variable => {
            this.cssVariablesDefaults.set(variable.variable, variable.defaultValue);
          });
          this.logger.debug('Loaded CSS variables defaults from ' + url);
        },
        error: (err) => this.logger.error('Error loading CSS variables defaults:', err)
      });
  }

  private listenForRouteChanges = () => {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: any) => {
      this.logger.trace('CssService.routeChange event:', {url: event.url, configId: this.currentConfig?.id});
      this.applyVariablesByRoute(event.url);
    });
  };

  /**
   * Load font URLs from the client configuration
   * @param config The client configuration object
   */
  loadFontUrls(config: ClientConfiguration): void {
    this.logger.trace('CssService.loadFonts:', {configId: config.id, fontUrls: config.fontUrls});
    const promises = CssService.defaultFonts.map(url => this.loadFont(url));
    if (config.fontUrls && config.fontUrls.length > 0) {
      promises.push(...config.fontUrls.map(url => this.loadFont(url)));
    }
    Promise.all(promises).then(() => {
      this.logger.debug('All fonts loaded');
    });
  }

  loadFonts(config: ClientConfiguration): void {
    if (config.fontIds && config.fontIds.length > 0) {
      this.logger.debug('Loading fonts from configuration', config.fontIds);

      // Load each font
      config.fontIds.forEach(fontId => {
        if (!this.loadedFontIds.has(fontId)) {
          this.fontService.getFontFile(fontId).subscribe({
            next: (font) => {
              if (font) {
                this.loadFontFile(font);
              }
            },
            error: (err) => {
              this.logger.error(`Failed to load font ${fontId}`, err);
            }
          });
        }
      });
    }
  }

  private loadFontFile(font: FontFile): void {
    if (!font.id || this.loadedFontIds.has(font.id)) {
      return;
    }

    // Create a style element for this font
    const styleElement = this.document.createElement('style');
    styleElement.setAttribute('type', 'text/css');
    styleElement.setAttribute('id', `font-${font.id}`);

    // Add the font-face CSS
    styleElement.innerHTML = this.generateFontFaceCSS(font);

    // Add the style element to the document head
    this.document.head.appendChild(styleElement);

    // Mark this font as loaded
    this.loadedFontIds.add(font.id);
    this.logger.debug(`Font loaded: ${font.fontFamily} (ID: ${font.id})`);
  }

  private generateFontFaceCSS(font: FontFile): string {
    return `
      @font-face {
        font-family: "${font.fontFamily}";
        src: url("${this.fontService.getContentUrl(font.id!)}") format("${this.getFontFormat(font.mimeType)}");
        font-weight: ${font.fontWeight};
        font-style: ${font.fontStyle};
      }
    `;
  }

  private getFontFormat(mimeType: string): string {
    if (mimeType.includes('woff2')) return 'woff2';
    if (mimeType.includes('woff')) return 'woff';
    if (mimeType.includes('ttf') || mimeType.includes('truetype')) return 'truetype';
    if (mimeType.includes('otf') || mimeType.includes('opentype')) return 'opentype';
    return 'woff'; // Default
  }

  /**
   * Update the configuration without reloading fonts
   * @param config The updated client configuration
   */
  updateConfiguration(config: ClientConfiguration): void {
    this.logger.trace('CssService.updateConfiguration:', {configId: config.id});
    this.currentConfig = config;
    this.applyVariablesByRoute(this.router.url);
    this.applyLogoAsHeaderBackground(config);
    this.notifyConfigChanged();
  }

  /**
   * Notify that the configuration has changed
   */
  private notifyConfigChanged(): void {
    this.configChangeSubject.next(Date.now());
    this.logger.debug('Notified about configuration change');
  }

  /**
   * Apply CSS variables based on current route
   * @param url Current route URL
   */
  private applyVariablesByRoute(url: string): void {
    if (!this.currentConfig) return;
    this.applyDefaultCssVariables();
    const isAdminPage = url.startsWith('/admin');
    if (!isAdminPage) {
      this.applyCssVariables(this.currentConfig);
    }
  }

  /**
   * Apply default values for all CSS variables
   */
  private applyDefaultCssVariables(): void {
    const root = this.document.documentElement;
    this.cssVariablesDefaults.forEach((value, variable) => {
      root.style.setProperty(variable, value);
    });
  }

  /**
   * Apply CSS variables from client configuration
   * @param config The client configuration
   */
  private applyCssVariables(config: ClientConfiguration): void {
    if (config.cssVariables) {
      this.logger.debug('Applying custom CSS variables:', config.cssVariables, {configId: config.id});
      const root = this.document.documentElement;
      Object.entries(config.cssVariables).forEach(([variable, value]) => {
        if (variable && value) {
          if (variable === '--tw-font-family') {
            this.applyFontFamily(value);
          } else {
            root.style.setProperty(variable, value);
          }
        }
      });
    }
  }

  /**
   * Set --tw-header-logo-url CSS variable from the logo component configuration,
   * so it can be used as a header background-image in custom CSS.
   */
  private applyLogoAsHeaderBackground(config: ClientConfiguration): void {
    const logoConfig = config.getConfigurationSafe('LOGO');
    const logoImageId = logoConfig?.getNumber('logoImage');
    const root = this.document.documentElement;
    if (logoImageId) {
      const logoUrl = httpUrl(`/images/logo/${logoImageId}/content`);
      root.style.setProperty('--tw-header-logo-url', `url(${logoUrl})`);
      this.logger.debug('Set --tw-header-logo-url:', logoUrl);
    } else {
      root.style.removeProperty('--tw-header-logo-url');
    }
  }

  /**
   * Apply font family with proper checks
   * @param fontFamily Font family to apply
   */
  private applyFontFamily(fontFamily: string): void {
    const root = this.document.documentElement;
    if (this.isFontAvailable(fontFamily)) {
      root.style.setProperty('--tw-font-family', fontFamily);
    } else {
      const fontWithFallbacks = `${fontFamily}, sans-serif`;
      root.style.setProperty('--tw-font-family', fontWithFallbacks);
      this.logger.warn(`Font "${fontFamily}" may not be available, added fallbacks: ${fontWithFallbacks}`);
    }
  }

  /**
   * Load a single font URL and return a promise that resolves when the font is loaded
   * @param url The font URL to load
   * @returns A promise that resolves when the font is loaded
   */
  private loadFont(url: string): Promise<void> {
    // Return existing promise if this font is already being loaded
    if (this.fontLoadPromises.has(url)) {
      return this.fontLoadPromises.get(url)!;
    }

    // Skip if already loaded
    if (this.loadedFonts.has(url)) {
      return Promise.resolve();
    }

    // Create a new promise for loading this font
    const loadPromise = new Promise<void>((resolve, reject) => {
      const head = this.document.head;
      const link = this.document.createElement('link');
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-dynamic-font', url);

      // Set up load and error handlers
      link.onload = () => {
        this.logger.debug('Font loaded:', url);
        this.loadedFonts.add(url);
        resolve();
      };

      link.onerror = (error) => {
        this.logger.error('Error loading font:', url, error);
        reject(error);
      };

      head.appendChild(link);
    });

    // Store the promise for this URL
    this.fontLoadPromises.set(url, loadPromise);

    // Clean up the promise after it resolves or rejects
    loadPromise.finally(() => {
      this.fontLoadPromises.delete(url);
    });

    return loadPromise;
  }

  /**
   * Check if a font is actually available in the browser
   * @param fontFamily The font family name to check
   * @returns True if the font is available
   */
  private isFontAvailable(fontFamily: string): boolean {
    if (CssService.webSafeFonts.some(font =>
      fontFamily.toLowerCase().includes(font.toLowerCase()))) {
      return true;
    }

    // For other fonts, use the font detection technique
    const testString = 'abcdefghijklmnopqrstuvwxyz0123456789';
    const testSize = '72px';
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return false;

    context.font = `${testSize} serif`;
    const serifWidth = context.measureText(testString).width;

    context.font = `${testSize} sans-serif`;
    const sansWidth = context.measureText(testString).width;

    context.font = `${testSize} ${fontFamily}, serif`;
    const testWidth = context.measureText(testString).width;

    // If the width with the test font is different from both serif and sans-serif,
    // or exactly matches one of them when the font name contains that style,
    // then the font is likely available
    const isSerif = fontFamily.toLowerCase().includes('serif');
    const isSans = fontFamily.toLowerCase().includes('sans');

    return (testWidth !== serifWidth && testWidth !== sansWidth) ||
      (isSerif && testWidth === serifWidth) ||
      (isSans && testWidth === sansWidth);
  }

  getAvailableCssVariables = () => this.availableCssVariables;
}
